import api from '@/api';
import dayjs from 'dayjs';
import { saveParamsToQuery } from '@/utils/helpers';
import fi from 'air-datepicker/locale/fi';

let currentTicketListRequest = null;

const lots = {
  namespaced: true,

  state() {
    return {
      isLoading: false,
      filterParams: {
        object: [],
        region: [],
        status: [],
        categories: [],
      },
      filterParamsHistory: [],
      selectedFilters: {
        object: [],
        region: [],
        status: [],
        categories: [],
      },
      selectedFiltersHistory: {},
      search: '',
      list: {
        lots: [],
        pageCount: 0,
        page: 0,
      },
      lotHistory: [],
      lotDetail: [],
      clarificationDetail: {},
      selectedFiltersDocuments: {},
      documents: {
        documents: [],
        pageCount: 0,
        page: 0,
      },
    };
  },

  mutations: {
    setFilterParams(state, params) {
      state.filterParams = params.result;
    },

    setPage(state, page) {
      state.list.page = page;
    },

    setFilterParamsHistory(state, histList) {
      const filters = [];
      histList?.forEach((item) => {
        if (filters.find((filter) => filter.value === item.field)) {
          return;
        }
        const value = item.field;
        const label = item.fieldName;
        filters.push({ value, label });
      });
      state.filterParamsHistory = filters;
    },

    setSelectedFilters(state, params) {
      if (params?.publicationDate) {
        params.publicationDateStart = params.publicationDate;
        params.publicationDateEnd = params.publicationDate;
      } else {
        delete params.publicationDateStart;
        delete params.publicationDateEnd;
      }

      state.selectedFilters = params;
    },

    setSelectedFiltersHistory(state, params) {
      state.selectedFiltersHistory = params;
    },

    clearSelectedFiltersHistory(state) {
      state.selectedFiltersHistory = {};
    },

    setSearch(state, value) {
      state.search = value;
    },

    setList(state, data) {
      state.list = { ...data.result };
    },

    setDocumentsList(state, data) {
      state.documents = { ...data.result };
    },

    setDocumentsPage(state, page) {
      state.documents.page = page;
    },

    changeLoading(state, status) {
      state.isLoading = status;
    },

    setLotHistory(state, value) {
      state.lotHistory = value;
    },

    setClarificationDetail(state, value) {
      state.clarificationDetail = value;
    },

    setLotDetail(state, value) {
      state.lotDetail = value;
    },

    setFilterDocuments(state, filters = {}) {
      state.selectedFiltersDocuments = { ...filters };
    },
  },

  actions: {
    async setSearch({ commit, dispatch }, value) {
      commit('setSearch', value);
      commit('setPage', 1);
      await dispatch('loadList');
    },
    async setPage({ commit, dispatch }, page) {
      commit('setPage', page);
      await dispatch('loadList');
    },
    async setDocumentsPage({ commit, dispatch }, page) {
      commit('setDocumentsPage', page);
      await dispatch('loadDocuments');
    },
    async setSelectedFilters({ commit, dispatch }, params) {
      commit('setSelectedFilters', params);
      await dispatch('loadList');
    },
    async setSelectedFiltersHistory({ commit, dispatch }, params) {
      commit('setSelectedFiltersHistory', params);
      await dispatch('loadList');
    },
    async clearSelectedFiltersHistory({ commit, dispatch }) {
      commit('clearSelectedFiltersHistory');
      await dispatch('loadList');
    },
    async supplyOffer({ dispatch }, offer) {
      await api.lots.supplier.supplyOffer(offer);
    },
    async clarificationCreate({ rootState, rootGetters }, clarification) {
      if (clarification.clarificationId) {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!role && isLoggedIn) {
          return;
        }
        await api.lots[role].clarificationAddMessage(clarification);
      } else {
        await api.lots.supplier.clarificationCreate(clarification);
      }
    },
    async loadClarificationDetail({
      dispatch, commit, state, rootState, rootGetters,
    }, { lotId, id }) {
      if (id) {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!role && isLoggedIn) {
          return;
        }
        const { data } = await api.lots[role].clarificationDetail(id);
        commit('setClarificationDetail', data.result);
      } else {
        await dispatch('loadLotDetail', { id: lotId });
        commit('setClarificationDetail', { general: state.lotDetail.general });
      }
    },
    async loadFilterParams({ commit }) {
      try {
        const { data } = await api.lots.pub.filterParams();
        commit('setFilterParams', data);
      } catch (e) {
        console.log(e);
      }
    },
    async toggleFavoriteLot({ rootGetters }, id) {
      try {
        const role = rootGetters['user/getPriorityRole']();
        if (role === 'pub') {
          return false;
        }
        await api.lots[role].favorite(id);
        return true;
      } catch (e) {
        console.warn('toggleFavoriteLot', e);
        return false;
      }
    },
    async loadDocuments({ commit, state }) {
      commit('changeLoading', true);

      try {
        const params = {};

        // Формируем параметры запроса, удаляем пустые ключи фильтров
        Object.entries({
          page: state.documents.page,
          search: state.search,
          ...state.selectedFiltersDocuments,
        }).forEach(([key, value]) => {
          if (typeof value === 'number') {
            params[key] = value;
            return;
          }
          if (typeof value === 'object' && value !== null && Object.keys(value).length) {
            params[key] = value;
            return;
          }
          if (value) {
            params[key] = value;
          }
        });
        params.pageSize = 15;

        const { data } = await api.user.getDocuments(params);

        commit('setDocumentsList', data);
      } catch (e) {
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadList({
      state, commit, rootState, rootGetters,
    }) {
      commit('changeLoading', true);

      try {
        if (currentTicketListRequest) {
          return;
        }
        const { isLoggedIn } = rootState.user;
        const { filterType } = rootState.appService;
        const role = rootGetters['user/getPriorityRole']();

        if (!role && isLoggedIn) {
          return;
        }

        const params = {};

        // Формируем параметры запроса, удаляем пустые ключи фильтров
        Object.entries({
          page: state.list.page,
          search: state.search,
          ...state.selectedFilters,
        }).forEach(([key, value]) => {
          if (typeof value === 'number') {
            params[key] = value;
            return;
          }
          if (typeof value === 'object' && value !== null && Object.keys(value).length) {
            params[key] = value;
            return;
          }
          if (value) {
            params[key] = value;
          }
        });
        params.pageSize = isLoggedIn ? 10 : 4;
        if (filterType) {
          params.type = filterType;
        }
        if (api.lots[role] !== undefined) {
          currentTicketListRequest = api.lots[role].list(params);
          const { data } = await currentTicketListRequest;
          saveParamsToQuery({ page: params.page, search: params.search });
          commit('setList', data);
          currentTicketListRequest = null;
        } else {
          commit('setList', { result: { lots: [], page: 1, pageCount: 1 } });
        }
      } catch (e) {
        currentTicketListRequest = null;
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadFavoriteList({
      state, commit, rootState, rootGetters,
    }) {
      commit('changeLoading', true);

      try {
        if (currentTicketListRequest) {
          return;
        }
        const { isLoggedIn } = rootState.user;
        const { filterType } = rootState.appService;
        const role = rootGetters['user/getPriorityRole']();

        if (!role && isLoggedIn) {
          return;
        }

        const params = {};

        // Формируем параметры запроса, удаляем пустые ключи фильтров
        Object.entries({
          page: state.list.page,
          search: state.search,
          ...state.selectedFilters,
        }).forEach(([key, value]) => {
          if (typeof value === 'number') {
            params[key] = value;
            return;
          }
          if (typeof value === 'object' && value !== null && Object.keys(value).length) {
            params[key] = value;
            return;
          }
          if (value) {
            params[key] = value;
          }
        });
        params.pageSize = isLoggedIn ? 10 : 4;
        if (filterType) {
          params.type = filterType;
        }

        currentTicketListRequest = api.lots[role].favoriteList(params);
        const { data } = await currentTicketListRequest;
        saveParamsToQuery({ page: params.page, search: params.search });
        commit('setList', data);
        currentTicketListRequest = null;
      } catch (e) {
        currentTicketListRequest = null;
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },
    async loadMyLotsList({
      state, commit, rootState, rootGetters,
    }) {
      commit('changeLoading', true);

      try {
        if (currentTicketListRequest) {
          return;
        }
        const { isLoggedIn } = rootState.user;
        const { filterType } = rootState.appService;
        const role = rootGetters['user/getPriorityRole']('supplier');

        if (!role && isLoggedIn) {
          return;
        }

        const params = {};

        // Формируем параметры запроса, удаляем пустые ключи фильтров
        Object.entries({
          page: state.list.page,
          search: state.search,
          ...state.selectedFilters,
        }).forEach(([key, value]) => {
          if (typeof value === 'number') {
            params[key] = value;
            return;
          }
          if (typeof value === 'object' && value !== null && Object.keys(value).length) {
            params[key] = value;
            return;
          }
          if (value) {
            params[key] = value;
          }
        });
        params.pageSize = isLoggedIn ? 10 : 4;
        if (filterType) {
          params.type = filterType;
        }

        currentTicketListRequest = api.lots[role].participateList(params);
        const { data } = await currentTicketListRequest;
        saveParamsToQuery({ page: params.page, search: params.search });
        commit('setList', data);
        currentTicketListRequest = null;
      } catch (e) {
        currentTicketListRequest = null;
        console.log(e);
      } finally {
        commit('changeLoading', false);
      }
    },

    async loadLotHistory({ commit, rootState, rootGetters }, { id }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']();

        if (!role && isLoggedIn) {
          return;
        }
        const { data } = await api.lots[role].history({ id });
        commit('setLotHistory', data.result);
        commit('setFilterParamsHistory', data.result);
      } catch (e) {
        console.log(e);
        commit('changeLoading', false);
      } finally {
        commit('changeLoading', false);
      }
    },

    async loadLotDetail({ commit, rootState, rootGetters }, { id }) {
      commit('changeLoading', true);
      try {
        const { isLoggedIn } = rootState.user;
        const role = rootGetters['user/getPriorityRole']();

        if (!role && isLoggedIn) {
          return;
        }
        const { data } = await api.lots[role].detail({ id });

        if (data.result?.offerItems?.lot?.applicationDate) {
          const date = dayjs(data.result.offerItems.lot.applicationDate);
          if (Math.abs(date.diff(dayjs(rootState.user.dynamicServerTime), 'second')) < 10) {
            data.result.offerItems.lot.applicationDate = null;
          }
        }

        commit('setLotDetail', data.result);
      } catch (e) {
        console.log(e);
        commit('changeLoading', false);
      } finally {
        commit('changeLoading', false);
      }
    },
  },

  getters: {
    getLotByID: (state) => (id) => state.list.lots.find((lot) => lot.id === Number(id)),
  },
};

export default lots;
